import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Save, Plus, Trash2, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ImageUpload from './ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_LABELS = {
  'accueil': 'Accueil',
  'mairie': 'Ma Mairie',
  'decouvrir': 'Découvrir',
  'vie-quotidienne': 'Vie Quotidienne',
  'diaspora': 'Diaspora',
};

const SectionEditor = ({ section, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const hasItems = Array.isArray(section.items);

  return (
    <Card className="border-l-4 border-l-emerald-500" data-testid={`section-${section.key}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base font-semibold text-emerald-800">{section.title}</CardTitle>
            <p className="text-xs text-neutral-400">Clé : {section.key}</p>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" disabled={isFirst} onClick={onMoveUp} title="Monter">
              <ChevronUp size={14} />
            </Button>
            <Button variant="ghost" size="sm" disabled={isLast} onClick={onMoveDown} title="Descendre">
              <ChevronDown size={14} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={onDelete}
              title="Supprimer cette section"
              data-testid={`delete-section-${section.key}`}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <label className="block text-sm font-medium mb-1">Titre affiché</label>
          <Input
            value={section.title}
            onChange={(e) => onChange({ ...section, title: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Contenu texte</label>
          <Textarea
            rows={4}
            value={section.content || ''}
            onChange={(e) => onChange({ ...section, content: e.target.value })}
            placeholder="Texte de cette section..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Image (optionnel)</label>
          <ImageUpload
            value={section.image_url || ''}
            onChange={(url) => onChange({ ...section, image_url: url })}
            placeholder="Ajouter une image"
          />
        </div>

        {hasItems && (
          <div>
            <label className="block text-sm font-medium mb-2">Éléments de liste</label>
            <div className="space-y-2">
              {section.items.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-start bg-neutral-50 p-3 rounded-lg">
                  <GripVertical size={16} className="text-neutral-300 mt-2 flex-shrink-0" />
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    {Object.keys(item).map((field) => (
                      <div key={field}>
                        <label className="text-xs text-neutral-500 capitalize">{field}</label>
                        <Input
                          value={item[field] || ''}
                          onChange={(e) => {
                            const newItems = [...section.items];
                            newItems[idx] = { ...newItems[idx], [field]: e.target.value };
                            onChange({ ...section, items: newItems });
                          }}
                          className="text-sm"
                        />
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 flex-shrink-0"
                    onClick={() => {
                      const newItems = section.items.filter((_, i) => i !== idx);
                      onChange({ ...section, items: newItems });
                    }}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const template = section.items.length > 0
                    ? Object.fromEntries(Object.keys(section.items[0]).map(k => [k, '']))
                    : { title: '', description: '' };
                  onChange({ ...section, items: [...section.items, template] });
                }}
              >
                <Plus size={14} className="mr-1" /> Ajouter un élément
              </Button>
            </div>
          </div>
        )}

        {!hasItems && (
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => onChange({ ...section, items: [{ title: '', description: '' }] })}
          >
            <Plus size={12} className="mr-1" /> Transformer en liste
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const PageEditor = ({ getAuthHeaders }) => {
  const [pages, setPages] = useState({});
  const [activeSlug, setActiveSlug] = useState('accueil');
  const [saving, setSaving] = useState(false);

  const fetchPages = useCallback(async () => {
    try {
      const slugs = Object.keys(PAGE_LABELS);
      const responses = await Promise.all(
        slugs.map(slug => axios.get(`${API}/pages/${slug}`))
      );
      const pagesData = {};
      slugs.forEach((slug, i) => {
        pagesData[slug] = responses[i].data;
      });
      setPages(pagesData);
    } catch (error) {
      console.error('Error fetching pages:', error);
    }
  }, []);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const handleSectionChange = (slug, idx, updated) => {
    setPages(prev => {
      const sections = [...prev[slug].sections];
      sections[idx] = updated;
      return { ...prev, [slug]: { ...prev[slug], sections } };
    });
  };

  const handleAddSection = (slug) => {
    const key = `section_${Date.now()}`;
    const newSection = { key, title: 'Nouvelle section', content: '', image_url: null, items: null };
    setPages(prev => ({
      ...prev,
      [slug]: { ...prev[slug], sections: [...prev[slug].sections, newSection] }
    }));
    toast.info('Section ajoutée. N\'oubliez pas d\'enregistrer.');
  };

  const handleDeleteSection = (slug, idx) => {
    const section = pages[slug].sections[idx];
    if (!window.confirm(`Supprimer la section "${section.title}" ?`)) return;
    setPages(prev => ({
      ...prev,
      [slug]: { ...prev[slug], sections: prev[slug].sections.filter((_, i) => i !== idx) }
    }));
    toast.info('Section supprimée. N\'oubliez pas d\'enregistrer.');
  };

  const handleMoveSection = (slug, idx, direction) => {
    setPages(prev => {
      const sections = [...prev[slug].sections];
      const target = idx + direction;
      [sections[idx], sections[target]] = [sections[target], sections[idx]];
      return { ...prev, [slug]: { ...prev[slug], sections } };
    });
  };

  const handleSave = async (slug) => {
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/admin/pages/${slug}`, {
        sections: pages[slug].sections
      }, { headers });
      toast.success(`Page "${PAGE_LABELS[slug]}" enregistrée !`);
      await fetchPages();
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="page-editor">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-heading font-bold">Gestion des Pages</h2>
      </div>

      <Tabs value={activeSlug} onValueChange={setActiveSlug}>
        <TabsList className="bg-neutral-100 flex-wrap h-auto py-1">
          {Object.entries(PAGE_LABELS).map(([slug, label]) => (
            <TabsTrigger key={slug} value={slug} className="text-sm">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.keys(PAGE_LABELS).map((slug) => (
          <TabsContent key={slug} value={slug}>
            {pages[slug] ? (
              <div className="space-y-4">
                {/* Top action bar */}
                <div className="flex justify-between items-center gap-3 flex-wrap">
                  <Button
                    onClick={() => handleAddSection(slug)}
                    variant="outline"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid={`add-section-${slug}`}
                  >
                    <Plus size={16} className="mr-2" />
                    Ajouter une section
                  </Button>
                  <Button
                    onClick={() => handleSave(slug)}
                    disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`save-page-${slug}`}
                  >
                    <Save size={16} className="mr-2" />
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </div>

                {/* Sections */}
                {pages[slug].sections.length === 0 ? (
                  <div className="text-center py-12 text-neutral-400 border-2 border-dashed rounded-xl">
                    Aucune section. Cliquez sur "Ajouter une section" pour commencer.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pages[slug].sections.map((section, idx) => (
                      <SectionEditor
                        key={section.key}
                        section={section}
                        onChange={(updated) => handleSectionChange(slug, idx, updated)}
                        onDelete={() => handleDeleteSection(slug, idx)}
                        onMoveUp={() => handleMoveSection(slug, idx, -1)}
                        onMoveDown={() => handleMoveSection(slug, idx, 1)}
                        isFirst={idx === 0}
                        isLast={idx === pages[slug].sections.length - 1}
                      />
                    ))}
                  </div>
                )}

                {/* Bottom save */}
                <div className="flex justify-end pt-4">
                  <Button
                    onClick={() => handleSave(slug)}
                    disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Save size={16} className="mr-2" />
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-neutral-500">Chargement...</div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default PageEditor;
